import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Plus, Trash2, History, Pen, Eye, MoreVertical, Copy, Globe, ListChecks, Info } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";

import { Button, ButtonShadcn } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import SearchInput from "@/components/shared/SearchInput";
import { Filter, FilterPill } from "@/components/shared/Filter";
import AlertModal from "@/components/shared/AlertModal";
import { PaginationNew } from "@/helper/Pagination";
import PermissionGuard from "@/permissions/PermissionGuard";
import { getUserPermissions, hasPermission } from "@/permissions/rbac";
import { useAuth } from "@/hooks/useAuth";
import ModuleUnavailableNotice from "@/permissions/ModuleUnavailableNotice";
import useModuleAccess from "@/hooks/useModuleAccess";
import { MODULES } from "@/permissions/modules";
import { PERMISSIONS } from "@/permissions/constant";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { cn } from "@/lib/utils";

import { ComplianceService } from "@/services/complianceService";
import ChecklistVersionsModal from "./components/ChecklistVersionsModal";
import ChecklistReapplyModal from "./components/ChecklistReapplyModal";
import ChecklistBuilderModal from "./components/ChecklistBuilderModal";
import DeleteChecklistNotice from "./components/DeleteChecklistNotice";
import ChecklistCard from "./components/ChecklistCard";

// Cards per page. The options themselves live in `PaginationNew`, which draws
// the picker; this is only the default the page starts on.
const DEFAULT_PAGE_SIZE = 10;

const CardSkeleton = () => (
  <Card className="p-4 flex flex-col gap-3">
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 flex flex-col gap-1.5">
        <div className="h-4 w-2/3 rounded bg-background-icon/70 animate-pulse" />
        <div className="h-3 w-1/3 rounded bg-background-icon/50 animate-pulse" />
      </div>
      <div className="h-5 w-14 rounded-full bg-background-icon/60 animate-pulse" />
    </div>
    <div className="h-24 w-full rounded-xl bg-background-icon/40 animate-pulse" />
  </Card>
);

/**
 * The review notice on a country document set (CM-7).
 *
 * Two different things raise it and the officer sees ONE message either way,
 * because their next action is identical - go and look at the set:
 *
 *   wasRestored     a SuperAdmin unticked this country and reticked it, so the
 *                   tenant has their own personalised set back
 *   templateMovedOn the platform's set for this country has changed since this
 *                   copy was seeded. The copy is never overwritten, so this
 *                   notice is the only way they learn there is something new
 *
 * Dismissal is handed up rather than handled here: the notice lives on the row
 * and a local-only dismiss would reappear on the next load.
 */
// Exported for its own component test - rendering the whole list screen to
// reach it would put a fetch and a router between the test and the rules.
export const CountryMasterReviewNotice = ({ wasRestored = false, templateMovedOn = false, needsReview = false, writesBlocked = false, onDismiss }) => {
  if (!needsReview) return null;

  // One sentence assembled from the halves that are true, rather than two
  // banners. Both true is the ordinary case after a long revocation, and
  // stacking them costs attention and buys nothing.
  const parts = [];
  if (wasRestored) {
    parts.push("This country set was restored, so it is available again.");
  }
  if (templateMovedOn) {
    parts.push("The platform's set for this country has been updated since your copy was created.");
  }
  parts.push("Check whether yours needs updating.");

  return (
    <div role="status" className="flex items-start gap-2 rounded-md border border-tone-amber/40 bg-tone-amber-soft/40 px-2.5 py-2" onClick={(e) => e.stopPropagation()}>
      <Info size={13} className="mt-0.5 shrink-0 text-tone-amber" />
      <p className="text-[11.5px] leading-snug text-text-primary/80 flex-1">{parts.join(" ")}</p>
      <ButtonShadcn
        variant="link"
        disabled={writesBlocked}
        onClick={onDismiss}
        className="h-auto w-auto shrink-0 p-0 text-[11px] font-medium text-tone-amber hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
      >
        Dismiss
      </ButtonShadcn>
    </div>
  );
};

/**
 * The card's "..." menu. Replaces the pair of always-visible edit/delete icons:
 * three actions now fit (view / edit / delete) without the card growing a
 * toolbar. Closes on outside click and on Escape.
 */
// Exported for its own component test: the country-set rules (template first,
// no delete, edit behind its own permission) all live here, and driving the
// whole list screen to reach them would put a fetch and a router in the way.
export const ChecklistCardActionsMenu = ({ onView, onEdit, onHistory, onDelete, onUseTemplate, writesBlocked, isCountryMaster = false, canManageMasters = true }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const item = (icon, label, onClick, danger = false, disabled = false) => (
    <Button
      type="button"
      disabled={disabled}
      onClick={() => {
        setOpen(false);
        onClick();
      }}
      className={cn(
        "w-full flex items-center gap-2 px-3 py-2 text-xs text-left rounded-none justify-start",
        danger ? "text-tone-rose hover:bg-tone-rose-soft/40" : "text-text-primary hover:bg-background-tertiary",
        "disabled:opacity-40 disabled:cursor-not-allowed",
      )}
    >
      {icon}
      {label}
    </Button>
  );

  return (
    <div ref={containerRef} className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
      <Button
        type="button"
        title="Checklist actions"
        onClick={() => setOpen((p) => !p)}
        className="h-8 w-8 !p-0 rounded-lg flex items-center justify-center text-text-primary/50 hover:text-text-primary hover:bg-background-icon"
      >
        <MoreVertical size={15} />
      </Button>

      {open && (
        <div className="absolute right-0 top-9 z-50 w-48 min-w-[190px] overflow-hidden rounded-xl border border-border-primary bg-background-secondary shadow-xl animate-in fade-in duration-150">
          {/* A country document set is a SOURCE, so the first thing
                        offered is building from it — that is what nearly every
                        officer opening this menu wants, and it needs no
                        permission beyond the one that shows the page. */}
          {isCountryMaster && item(<Copy size={13} />, "Use this template", onUseTemplate, false, writesBlocked)}
          {item(<Eye size={13} />, "View details", onView)}
          {/* Editing a master needs its own permission: it is the
                        source every future checklist for that country starts
                        from. Without it the row still opens read-only, so the
                        officer can see what they would be starting from. */}
          {item(
            <Pen size={13} />,
            writesBlocked || (isCountryMaster && !canManageMasters) ? "View checklist" : isCountryMaster ? "Edit country documents" : "Edit checklist",
            onEdit,
          )}
          {/* History is a READ, so it stays available even when the
                        module is dark — looking at what a checklist used to
                        contain changes nothing. */}
          {item(<History size={13} />, "Check history", onHistory)}
          {/* A country set is never deletable (Phase 4). Hiding the
                        item rather than showing a disabled one: there is no
                        state in which it becomes available, so offering it
                        would only invite the click the server refuses. */}
          {!isCountryMaster && item(<Trash2 size={13} />, "Delete checklist", onDelete, true, writesBlocked)}
        </div>
      )}
    </div>
  );
};

const ComplianceChecklists = () => {
  const setHeader = useOutletContext();

  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  // Filter panel (frontend-only until BACKEND_REQUIREMENTS #2 ships).
  const [showFilters, setShowFilters] = useState(false);
  const [filterRegion, setFilterRegion] = useState("");
  const [regionOptions, setRegionOptions] = useState([]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setPage(1);
  };

  const [builderOpen, setBuilderOpen] = useState(false);
  // Which checklist's version history is open (Phase 5F).
  const [historyTarget, setHistoryTarget] = useState(null);
  // The pending checklist edit, handed up by the builder (Phase 5D/5F).
  //
  // Held HERE rather than inside the builder so the two dialogs are siblings
  // and never overlap: saving closes the builder and opens the wizard; Back
  // closes the wizard and reopens the builder. Because the draft lives on
  // this page it survives that round trip, so Back is still lossless.
  const [reapply, setReapply] = useState(null);
  // The draft to reinstate when the builder reopens after Back.
  const [restoreDraft, setRestoreDraft] = useState(null);
  // Set by "Use this template" on a country document set: the builder opens
  // with this country already chosen, which is what runs the prefill.
  const [prefillRegion, setPrefillRegion] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Pre-flight for the delete dialog (Phase A). The server has always refused
  // to delete a checklist a candidate is assigned to, but the officer only
  // found out after clicking Delete, and then only as "one or more". This
  // reads the usage endpoint as the dialog opens so the refusal is stated
  // before the click, with the count and the names.
  //
  // It is an ACCELERATOR, never the authority (Hard Rule 4): a recruiter can
  // assign this checklist between the read and the click, so the 409 stays
  // the decider and re-renders the same copy from the count it carries.
  const [deleteUsage, setDeleteUsage] = useState(null);
  const [deleteUsageLoading, setDeleteUsageLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Entitlement is the route's job. Availability is this page's: a killed
  // Compliance refuses every non-GET with 503, so the checklists still read
  // and every control that writes one is disabled.
  const { isAvailable } = useModuleAccess(MODULES.COMPLIANCE);
  const writesBlocked = !isAvailable;

  useEffect(() => {
    setHeader({ title: "Compliance Checklists", subTitle: "Manage document checklists" });
    return () => setHeader({ title: "", subTitle: "" });
  }, [setHeader]);

  // One request for the whole page. The summary response carries counts but
  // an empty `sections` array (BACKEND_REQUIREMENTS #1), so the per-card
  // document list stays empty until that ships — the card falls back to the
  // counts rather than firing a GET per checklist (N+1 on every load).
  const fetchChecklists = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ComplianceService.getChecklists();
      setChecklists(res?.checklists ?? []);
    } catch (e) {
      console.error(e);
      toast.error(apiErrorMessageFormat(e, "Failed to load checklists"));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleToggleStatus = async (cl) => {
    if (writesBlocked) return;
    const newStatus = !cl.isActive;
    // Optimistic UI update
    setChecklists((prev) => prev.map((item) => (item.id === cl.id ? { ...item, isActive: newStatus } : item)));
    try {
      await ComplianceService.updateChecklist(cl.id, {
        name: cl.name,
        region: cl.region,
        description: cl.description,
        isActive: newStatus,
      });
      toast.success(`Checklist marked as ${newStatus ? "Active" : "Inactive"}`);
    } catch (err) {
      console.error(err);
      toast.error(apiErrorMessageFormat(err, "Failed to update status"));
      fetchChecklists();
    }
  };

  // CM-7. Clearing the notice is a server-side write on the row, not a local
  // hide: the flag lives on the checklist, so a local-only dismiss would come
  // straight back on the next load and read as a broken button.
  const handleDismissReview = async (cl) => {
    if (writesBlocked) return;
    setChecklists((prev) => prev.map((item) => (item.id === cl.id ? { ...item, needsReview: false, wasRestored: false, templateMovedOn: false } : item)));
    try {
      await ComplianceService.dismissMasterReview(cl.id);
    } catch (err) {
      console.error(err);
      toast.error(apiErrorMessageFormat(err, "Failed to dismiss the notice"));
      fetchChecklists();
    }
  };

  useEffect(() => {
    fetchChecklists();
  }, [fetchChecklists]);

  // Regions in use, for the filter dropdown. Refetched alongside the list so
  // a newly-created checklist's region appears without a reload.
  useEffect(() => {
    let active = true;
    ComplianceService.getChecklistRegions()
      .then((regions) => {
        if (active) setRegionOptions(regions.map((r) => ({ value: r, label: r })));
      })
      .catch(() => {
        if (active) setRegionOptions([]);
      });
    return () => {
      active = false;
    };
  }, [checklists]);

  // Name only. Searching document names is not available here and is not a
  // gap to fill: the list response carries `sections: []` by decision, because
  // loading the tree made this endpoint's cost scale with every document in
  // the tenant to produce three counts (repository.list_checklists). Region
  // moved to the filter panel, where a dropdown of real values beats a
  // free-text box a typo silently defeats.
  const filteredChecklists = useMemo(() => {
    const q = query.trim().toLowerCase();
    const region = filterRegion.trim().toLowerCase();

    return checklists.filter((cl) => {
      if (q && !cl.name?.toLowerCase().includes(q)) return false;
      // Exact: the region comes from a dropdown of real values, so a
      // substring match would let "United States" also match a search
      // for a region whose name it happens to contain.
      if (region && cl.region?.toLowerCase() !== region) return false;
      return true;
    });
  }, [checklists, query, filterRegion]);

  // Country document sets vs the tenant's own checklists (Phase 6 grouping).
  // Split after filtering so the search and the region filter still cover
  // both groups at once instead of only the one currently in view.
  const masterChecklists = useMemo(() => filteredChecklists.filter((cl) => cl.isCountryMaster), [filteredChecklists]);
  const regularChecklists = useMemo(() => filteredChecklists.filter((cl) => !cl.isCountryMaster), [filteredChecklists]);

  // A narrowed list can be shorter than the current page.
  useEffect(() => {
    setPage(1);
  }, [query, filterRegion]);

  // Only the tenant's own checklists paginate. Country document sets are
  // bounded to however many countries the tenant has been given (six at
  // most, §2.5), so a page control on them would be overhead with nothing
  // to page through.
  const paginatedChecklists = useMemo(() => regularChecklists.slice((page - 1) * pageSize, page * pageSize), [regularChecklists, page, pageSize]);

  const filters = useMemo(
    () => [
      {
        // A dropdown of regions that actually have a checklist, rather
        // than a free-text box where a typo silently matches nothing.
        type: "select",
        key: "region",
        label: "All regions",
        value: filterRegion,
        onChange: setFilterRegion,
        options: regionOptions,
      },
    ],
    [filterRegion, regionOptions],
  );

  const activeFilters = useMemo(
    () =>
      [
        filterRegion && {
          key: "region",
          label: `Region: ${filterRegion}`,
          clear: () => setFilterRegion(""),
        },
      ].filter(Boolean),
    [filterRegion],
  );

  const clearAllFilters = () => {
    setFilterRegion("");
  };

  const openCreate = () => {
    setEditingId(null);
    setViewMode(false);
    setBuilderOpen(true);
  };

  // A plain "New Checklist" starts blank; only "Use this template" seeds a
  // country. Cleared here so the last template used does not leak into the
  // next blank build.
  const openBlankCreate = () => {
    setPrefillRegion("");
    openCreate();
  };

  // The country "Use this template" asked for. Handed to the builder, which
  // preselects it and runs the Phase 5 prefill.
  const openEdit = (id) => {
    setEditingId(id);
    setViewMode(false);
    setBuilderOpen(true);
  };

  const openView = (id) => {
    setEditingId(id);
    setViewMode(true);
    setBuilderOpen(true);
  };

  const closeBuilder = () => {
    setBuilderOpen(false);
    setEditingId(null);
    setViewMode(false);
  };

  // Editing a country document set is its own permission — reading one is
  // not. An officer without it still sees every set and still builds
  // checklists from them.
  const { user } = useAuth();
  const canManageMasters = hasPermission(PERMISSIONS.COMPLIANCE_MASTER_MANAGE, getUserPermissions(user));

  // "Use this template" opens the builder with the country preselected, which
  // is what fires the Phase 5 prefill. One prefill path, not two: the builder
  // already knows how to turn a country into a starting tree, and a second
  // route into the same behaviour is a second thing to keep correct.
  const openFromTemplate = (master) => {
    setRestoreDraft(null);
    setPrefillRegion(master.region || "");
    openCreate();
  };

  // Any assigned candidate blocks the delete — terminal ones included. Their
  // record still references the tree that judged them, and a document can
  // arrive against it long after approval (a maintenance check years later),
  // at which point the officer needs this checklist to still exist. Retiring
  // it from future use is what the Active switch is for.
  const deleteBlocked = (deleteUsage?.candidateCount ?? 0) > 0;

  // A country document set can never be deleted (ATH-I1765), whatever the
  // counts say. Read from the same pre-flight rather than a second request,
  // and falling back to the row the officer clicked so the dialog is right
  // even when the usage read fails.
  const deleteProtected = deleteUsage?.isCountryMaster ?? deleteTarget?.isCountryMaster ?? false;

  // Either refusal removes the Delete button; `protected` is the permanent one
  // and the notice checks it first.
  const deleteRefused = deleteProtected || deleteBlocked;

  // Open the delete dialog and read who is assigned, so the dialog can refuse
  // before the click rather than after it.
  const openDelete = async (checklist) => {
    setDeleteTarget(checklist);
    setDeleteUsage(null);
    setDeleteUsageLoading(true);
    try {
      const usage = await ComplianceService.getChecklistUsage(checklist.id);
      setDeleteUsage(usage);
    } catch (e) {
      // A failed pre-flight must not block the attempt: the server still
      // decides. The dialog falls back to its plain confirmation and the
      // 409 explains itself if the checklist turns out to be in use.
      console.error(e);
      setDeleteUsage(null);
    } finally {
      setDeleteUsageLoading(false);
    }
  };

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteUsage(null);
    setDeleteUsageLoading(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await ComplianceService.deleteChecklist(deleteTarget.id);
      toast.success("Checklist deleted");
      closeDelete();
      fetchChecklists();
    } catch (e) {
      console.error(e);
      const code = e?.response?.data?.error_code;
      if (code === "COMPLIANCE_CHECKLIST_PROTECTED") {
        // A country document set, clicked through from a tab opened
        // before it was one (or before this build shipped). Same
        // treatment as the in-use race: keep the dialog open and let it
        // re-render as the permanent notice, because closing it would
        // discard the only explanation the officer gets.
        setDeleteUsage((current) => ({
          ...(current ?? {}),
          isCountryMaster: true,
          countryCode: e?.response?.data?.data?.countryCode ?? current?.countryCode ?? null,
        }));
        return;
      }
      const inUse = code === "COMPLIANCE_CHECKLIST_IN_USE";
      if (inUse) {
        // The race in the report: someone assigned this checklist after
        // the pre-flight read. Keep the dialog open and re-render it as
        // the blocking notice from the count the server just sent —
        // closing it would lose the only explanation the officer gets.
        const data = e?.response?.data?.data ?? {};
        setDeleteUsage((current) => ({
          ...(current ?? {}),
          candidateCount: data.candidateCount ?? current?.candidateCount ?? 1,
          inFlightCount: data.inFlightCount ?? current?.inFlightCount ?? 0,
        }));
      } else {
        toast.error(apiErrorMessageFormat(e, "Failed to delete checklist"));
        closeDelete();
      }
    } finally {
      setDeleting(false);
    }
  };

  const emptyState = (
    <Card className="p-10 flex flex-col items-center text-center gap-3">
      <div className="h-11 w-11 rounded-full bg-background-icon flex items-center justify-center">
        <ListChecks size={20} className="text-text-primary/50" />
      </div>
      <div>
        <p className="text-sm font-medium text-text-primary">No checklists yet</p>
        <p className="text-xs text-text-primary/60 mt-0.5">Create a checklist to define which documents candidates need to submit for compliance review.</p>
      </div>
      <Button
        onClick={openCreate}
        disabled={writesBlocked}
        className="bg-button-secondary text-text-base rounded-full px-4 py-2 flex items-center gap-1.5 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Plus size={14} /> New Checklist
      </Button>
    </Card>
  );

  return (
    <PermissionGuard permission={PERMISSIONS.COMPLIANCE_MANAGE}>
      <div className="flex flex-col gap-3 min-h-[calc(100vh-110px)]">
        <ModuleUnavailableNotice module={MODULES.COMPLIANCE} suffix="Existing checklists stay readable, and nothing you have already set up is changed." />

        {/* Tenant compliance settings used to sit here, above the
                    search row. Phase 9 moved them to Org Settings, their
                    permanent home now that page exists — this page is about
                    checklists, and a tenant-wide policy card on it was always
                    the temporary arrangement its own docstring described. */}
        <div className="flex items-center gap-2.5 pt-1">
          <SearchInput value={query} onChange={setQuery} placeholder="Search by checklist name..." />

          <Filter
            filters={filters}
            activeFilters={activeFilters}
            showFilters={showFilters}
            onToggle={() => setShowFilters((p) => !p)}
            onClearAll={clearAllFilters}
            renderActiveFilter={(f) => <FilterPill key={f.key} label={f.label} onRemove={f.clear} />}
          />

          <Button
            onClick={openBlankCreate}
            disabled={writesBlocked}
            className="bg-button-secondary text-text-base rounded-full px-4 py-2 flex items-center gap-1.5 text-xs font-medium whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={14} /> New Checklist
          </Button>
        </div>

        {/* Checklist cards — this region scrolls on its own so the
                    pagination bar below it can stay pinned to the bottom of
                    the page instead of trailing right after a short list. */}
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
          {loading ? (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <CardSkeleton key={i} />
              ))}
            </div>
          ) : checklists.length === 0 ? (
            emptyState
          ) : filteredChecklists.length === 0 ? (
            <Card className="p-8 text-center text-[13px] text-text-primary/60">No checklists match the current search or filters.</Card>
          ) : (
            <div className="flex flex-col gap-4">
              {/* Country document sets are read-only sources every
                                real checklist for that country is copied from —
                                kept apart from the tenant's own checklists so
                                "which of these can I edit freely" is never a
                                guess. Tinted with the same blue the card's own
                                "Country set" badge uses, so the badge and the
                                section read as one thing. */}
              {masterChecklists.length > 0 && (
                <section className="rounded-2xl bg-tone-blue-soft/10 border border-tone-blue/20 p-3 flex flex-col gap-2.5">
                  <div className="flex items-center justify-between flex-wrap gap-1">
                    <div className="flex items-center gap-1.5">
                      <Globe size={13} className="text-tone-blue" />
                      <span className="text-xs font-semibold tracking-wide text-text-primary">Country document sets</span>
                      <span className="text-[11px] text-text-primary/50">{masterChecklists.length}</span>
                    </div>
                    <span className="text-[11px] text-text-primary/60">Copy one of these to start a new checklist</span>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {masterChecklists.map((cl) => (
                      <ChecklistCard
                        key={cl.id}
                        cl={cl}
                        writesBlocked={writesBlocked}
                        canManageMasters={canManageMasters}
                        onView={() => openView(cl.id)}
                        onEdit={() => {
                          setRestoreDraft(null);
                          openEdit(cl.id);
                        }}
                        onHistory={() => setHistoryTarget(cl)}
                        onDelete={() => openDelete(cl)}
                        onUseTemplate={() => openFromTemplate(cl)}
                        onToggleStatus={() => handleToggleStatus(cl)}
                        onDismissReview={() => handleDismissReview(cl)}
                      />
                    ))}
                  </div>
                </section>
              )}

              {masterChecklists.length > 0 && regularChecklists.length > 0 && <div className="border-t border-border-primary" />}

              {regularChecklists.length > 0 && (
                <section className="flex flex-col gap-2.5">
                  <div className="flex items-center gap-1.5">
                    <ListChecks size={13} className="text-text-primary/50" />
                    <span className="text-xs font-semibold tracking-wide text-text-primary">Your checklists</span>
                    <span className="text-[11px] text-text-primary/50">{regularChecklists.length}</span>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {paginatedChecklists.map((cl) => (
                      <ChecklistCard
                        key={cl.id}
                        cl={cl}
                        writesBlocked={writesBlocked}
                        canManageMasters={canManageMasters}
                        onView={() => openView(cl.id)}
                        onEdit={() => {
                          setRestoreDraft(null);
                          openEdit(cl.id);
                        }}
                        onHistory={() => setHistoryTarget(cl)}
                        onDelete={() => openDelete(cl)}
                        onUseTemplate={() => openFromTemplate(cl)}
                        onToggleStatus={() => handleToggleStatus(cl)}
                        onDismissReview={() => handleDismissReview(cl)}
                      />
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>

        {/* Pinned footer: stays at the bottom of the page regardless
                    of how many cards are showing, instead of trailing right
                    after a short list. Paginates the tenant's own checklists
                    only — the country document sets above are unpaginated and
                    bounded, so a page control counting them in would show
                    pages with nothing left to turn to. */}
        {!loading && regularChecklists.length > 0 && (
          <div
            className="md:p-0 pb-1 pt-2 mt-auto sticky bottom-0 bg-background-primary/95 backdrop-blur-xs border-t border-border-primary/40 shrink-0 z-10"
            style={{ zoom: 0.95 }}
          >
            <PaginationNew
              className="bg-transparent"
              currentPage={page}
              totalItems={regularChecklists.length}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              label="checklists"
              hasPrev={page > 1}
              hasNext={page * pageSize < regularChecklists.length}
            />
          </div>
        )}

        <ChecklistVersionsModal open={!!historyTarget} checklistId={historyTarget?.id} checklistName={historyTarget?.name} onClose={() => setHistoryTarget(null)} />

        <ChecklistReapplyModal
          open={!!reapply}
          checklistId={reapply?.checklistId}
          checklistName={reapply?.checklistName}
          currentVersion={reapply?.loadedVersion}
          draft={reapply?.payload}
          usage={reapply?.usage}
          // Back: drop the wizard and reopen the builder on the same
          // checklist, carrying the draft so the officer's unsaved
          // edits come back with them.
          onBack={() => {
            const target = reapply;
            setReapply(null);
            // Reopen the builder WITH the officer's edits. Back is
            // a navigation step, never a discard.
            if (target?.checklistId) {
              setRestoreDraft(target.payload);
              openEdit(target.checklistId);
            }
          }}
          // Phase 10: nothing changed, so nothing was written.
          onNoop={() => {
            setReapply(null);
            setRestoreDraft(null);
            closeBuilder();
            toast("No changes to save.");
          }}
          // Applied: the edit is saved and the flow is over, so it ends on
          // this page. Every piece of carried state goes with it —
          // `restoreDraft` above all, because the builder reinstates it
          // without refetching, so a draft left behind would reopen showing a
          // tree that is already committed, against a stale `loadedVersion`
          // the next save would be refused for.
          onApplied={() => {
            setReapply(null);
            setRestoreDraft(null);
            closeBuilder();
            fetchChecklists();
          }}
        />

        <ChecklistBuilderModal
          open={builderOpen}
          checklistId={editingId}
          readOnly={viewMode}
          restoreDraft={restoreDraft}
          prefillRegion={prefillRegion}
          onClose={() => {
            setRestoreDraft(null);
            setPrefillRegion("");
            closeBuilder();
          }}
          onSaved={fetchChecklists}
          onRequestReapply={(handoff) => {
            // Close the builder FIRST, then open the wizard. Two
            // dialogs are never on screen together.
            closeBuilder();
            setReapply(handoff);
          }}
        />

        <AlertModal
          open={!!deleteTarget}
          title={deleteRefused ? "Cannot delete this checklist" : "Delete checklist?"}
          description={
            deleteTarget ? (
              <DeleteChecklistNotice
                checklistName={deleteTarget.name}
                usage={deleteUsage}
                loading={deleteUsageLoading}
                blocked={deleteBlocked}
                protectedMaster={deleteProtected}
              />
            ) : (
              ""
            )
          }
          contentClassName="max-w-[460px] w-full"
          descriptionClassName="text-xs text-text-secondary"
          actionText="Delete"
          // Blocked: the dialog becomes a notice with nothing to
          // confirm, so the destructive button goes away rather than
          // sitting there greyed out inviting a click. Cancel stays.
          hideAction={deleteRefused}
          // Still reading usage — don't offer Delete on an unknown.
          actionDisabled={deleteUsageLoading}
          loading={deleting}
          cancelText={deleteRefused ? "Close" : "Cancel"}
          onOpenChange={(o) => !o && closeDelete()}
          onAction={confirmDelete}
          onCancel={closeDelete}
          actionClassName="bg-destructive text-white"
        />
      </div>
    </PermissionGuard>
  );
};

export default ComplianceChecklists;
